import type { AdvancedCarrierInitializationDiscovery } from '@/services/api';

export const MAX_DISCOVERY_STEPS_PER_ACTION = 500;

export type CarrierDiscoveryStopReason =
  | 'complete'
  | 'rate_limited'
  | 'cancelled'
  | 'stalled'
  | 'step_limit';

export interface CarrierDiscoveryRunResult {
  lastState: AdvancedCarrierInitializationDiscovery | null;
  steps: number;
  stopReason: CarrierDiscoveryStopReason;
}

export async function runCarrierDiscovery(input: {
  advance: () => Promise<AdvancedCarrierInitializationDiscovery>;
  onProgress: (state: AdvancedCarrierInitializationDiscovery) => Promise<void> | void;
  shouldContinue?: () => boolean;
  maxSteps?: number;
}): Promise<CarrierDiscoveryRunResult> {
  const shouldContinue = input.shouldContinue ?? (() => true);
  const maxSteps = input.maxSteps ?? MAX_DISCOVERY_STEPS_PER_ACTION;
  let lastState: AdvancedCarrierInitializationDiscovery | null = null;

  for (let steps = 0; steps < maxSteps; steps += 1) {
    if (!shouldContinue()) {
      return { lastState, steps, stopReason: 'cancelled' };
    }

    lastState = await input.advance();
    if (!shouldContinue()) {
      return { lastState, steps: steps + 1, stopReason: 'cancelled' };
    }
    await input.onProgress(lastState);

    if (lastState.progress.complete) {
      return { lastState, steps: steps + 1, stopReason: 'complete' };
    }
    if (lastState.progress.status === 'rate_limited') {
      return { lastState, steps: steps + 1, stopReason: 'rate_limited' };
    }
    if (lastState.advancedPhase === null) {
      return { lastState, steps: steps + 1, stopReason: 'stalled' };
    }
  }

  return {
    lastState,
    steps: maxSteps,
    stopReason: 'step_limit',
  };
}
